import { Gift } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { HistorySection } from '@/features/audit-log';
import { usePermissions } from '@/features/auth';
import { PointsTiersSection } from '@/features/coupons';
import { useMarketTimeZone, useSelectedCountry } from '@/features/country-scope';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';

import { useLoyaltyRules } from '../api/loyalty.queries';
import { BaseRulesCard } from '../components/base-rules-card';
import { BrandRulesSection } from '../components/brand-rules-section';

/**
 * Marketing › Loyalty points › Configuration: what customers of the selected country earn on
 * registration and per purchased item. Read with loyalty.read, changed with loyalty.manage.
 * Points are spent only on points coupons (COUPONS_PLAN.md §6), configured below the earning rules.
 */
export function LoyaltyConfigurationPage() {
  const { country } = useSelectedCountry();
  const zone = useMarketTimeZone();
  const { can } = usePermissions();
  const canManage = can('loyalty.manage');
  const rules = useLoyaltyRules(country?.id);
  const countryName = country ? (country.nameEn ?? country.code ?? `#${country.id}`) : '';

  return (
    <PageContainer>
      <PageHeader
        title="Loyalty points"
        description="Per country: what customers earn on registration and per purchased item, brand rules, and the coupons their points buy."
      />
      {!country ? (
        <EmptyState
          icon={Gift}
          title="Choose a country"
          description="Earning rules are set per country."
        />
      ) : rules.isLoading ? (
        <Skeleton className="h-48" />
      ) : rules.error || !rules.data ? (
        <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />
      ) : (
        <>
          <BaseRulesCard
            countryName={countryName}
            rules={rules.data}
            canManage={canManage}
            zone={zone}
          />
          <BrandRulesSection
            countryId={country.id}
            countryName={countryName}
            defaultPoints={
              rules.data.defaultPurchase.isActive ? rules.data.defaultPurchase.points : null
            }
            canManage={canManage}
          />
          {can('coupons.read') && (
            <PointsTiersSection
              countryId={country.id}
              countryName={countryName}
              canManage={can('coupons.manage')}
            />
          )}
          {rules.data.registration.id !== null && (
            <HistorySection
              entityType="LoyaltyEarningRule"
              entityId={String(rules.data.registration.id)}
              title="Registration rule history"
              collapsible
              timeZone={zone}
            />
          )}
          {rules.data.defaultPurchase.id !== null && (
            <HistorySection
              entityType="LoyaltyEarningRule"
              entityId={String(rules.data.defaultPurchase.id)}
              title="Default purchase rule history"
              collapsible
              timeZone={zone}
            />
          )}
        </>
      )}
    </PageContainer>
  );
}
